'use client';

import { useIsProjectsEnabled } from '@/app-shell/flags/heidi-work-umbrella';
import { isMobileHeaderSheetOpenAtom } from '@/app-shell/layout/header/mobile-app-header/mobile-header-sheet-atoms';
import { routeChatIdFromQuery } from '@/app-shell/layout/Navigation/hooks/routeChatIdFromQuery';
import { streamingChatIdsAtom } from '@/atoms/chatAtoms';
import { useRenameChat } from '@/components/chat/hooks/useRenameChat';
import { useMutateDeleteChat } from '@/hooks/mutation/useMutateDeleteChat';
import { useInfiniteQueryChats } from '@/hooks/query/useInfiniteQueryChats';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useFilteredSavedChats, useSavedChats } from '@/hooks/useSavedChats';
import type { ChatListItem } from '@/types';
import {
  filterChatsForWorkAvailability,
  getChatHref,
  getChatListVersion,
} from '@heidi/agents/contracts';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const SEARCH_DEBOUNCE_MS = 300;

interface GroupedChats {
  recent: ChatListItem[];
  older: ChatListItem[];
}

interface UseEvidencePanelStateProps {
  onClose?: () => void;
}

export function useEvidencePanelState({ onClose }: UseEvidencePanelStateProps = {}) {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [chatToDelete, setChatToDelete] = useState<ChatListItem | null>(null);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const {
    editingState,
    editingTitle,
    setEditingTitle,
    handleStartRename,
    handleSubmitRename,
    handleCancelRename,
  } = useRenameChat();
  const setIsMobileHeaderSheetOpen = useSetAtom(isMobileHeaderSheetOpenAtom);
  const { isAuthenticated } = useIsAuthenticated();
  const {
    trackDeleteChatClicked,
    trackChatSaved,
    trackChatUnsaved,
    trackPastSessionClicked,
  } = useEvidenceAnalytics();
  const { isChatSaved, saveChat, unsaveChat } = useSavedChats();
  const isWorkEnabled = useIsProjectsEnabled();
  const { visibleSavedChats, filteredSavedChats, savedChatsPending } =
    useFilteredSavedChats(searchQuery, isWorkEnabled);

  // Get current chat ID from URL if on chat page
  const currentChatId = router.pathname.includes('/scribe/chat')
    ? routeChatIdFromQuery(router.query.id)
    : null;

  const {
    data: chatsData,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQueryChats({ query: debouncedSearchQuery, pageSize: 20 });
  const { mutate: deleteChatMutation, isPending: isDeleting } = useMutateDeleteChat();
  const streamingChatIds = useAtomValue(streamingChatIdsAtom);

  // Backend search already filters when a query is present.
  const chats = useMemo<ChatListItem[]>(() => {
    const pages = chatsData?.pages ?? [];
    return filterChatsForWorkAvailability(
      pages.flatMap((page) => page.chats),
      isWorkEnabled
    );
  }, [chatsData?.pages, isWorkEnabled]);

  const chatVersionById = useMemo(() => {
    const map = new Map<string, 1 | 2>();
    for (const chat of [...chats, ...visibleSavedChats]) {
      map.set(chat.id, getChatListVersion(chat));
    }
    return map;
  }, [chats, visibleSavedChats]);

  const groupedChats = useMemo<GroupedChats>(() => {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);

    const recent: ChatListItem[] = [];
    const older: ChatListItem[] = [];

    chats.forEach((chat) => {
      const chatDate = new Date(chat.updated_at);
      if (chatDate >= oneDayAgo) {
        recent.push(chat);
      } else {
        older.push(chat);
      }
    });

    return { recent, older };
  }, [chats]);

  const handleSelectChat = useCallback(
    (chatId: string) => {
      trackPastSessionClicked(chatId);
      void router.push(
        getChatHref({ id: chatId, version: chatVersionById.get(chatId) }),
        undefined,
        { shallow: false }
      );
      if (onClose) {
        onClose();
      } else {
        setIsMobileHeaderSheetOpen(false);
      }
    },
    [
      trackPastSessionClicked,
      router,
      onClose,
      setIsMobileHeaderSheetOpen,
      chatVersionById,
    ]
  );

  const handleDeleteChatClick = useCallback(
    (e: React.MouseEvent, chat: ChatListItem) => {
      e.stopPropagation();
      trackDeleteChatClicked(chat.id);
      setChatToDelete(chat);
    },
    [trackDeleteChatClicked]
  );

  const handleSaveChatClick = useCallback(
    (e: React.MouseEvent, chatId: string) => {
      e.stopPropagation();
      saveChat(chatId);
      trackChatSaved(chatId, 'sidebar');
    },
    [saveChat, trackChatSaved]
  );

  const handleUnsaveChatClick = useCallback(
    (e: React.MouseEvent, chatId: string, source: 'sidebar' | 'saved_section') => {
      e.stopPropagation();
      unsaveChat(chatId);
      trackChatUnsaved(chatId, source);
    },
    [unsaveChat, trackChatUnsaved]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!chatToDelete) return;
    deleteChatMutation(chatToDelete.id, {
      onSettled: () => {
        // Navigate away if we deleted the current chat
        if (currentChatId === chatToDelete.id) {
          void router.push('/scribe/chat', undefined, { shallow: false });
        }
        setChatToDelete(null);
      },
    });
  }, [chatToDelete, deleteChatMutation, currentChatId, router]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchQuery]);

  return {
    // State
    searchQuery,
    setSearchQuery,
    chatToDelete,
    setChatToDelete,
    showSignUpModal,
    setShowSignUpModal,
    isAuthenticated,
    currentChatId,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isDeleting,
    streamingChatIds,
    chats,
    groupedChats,
    filteredSavedChats,
    savedChatsPending,
    isWorkEnabled,
    // Handlers
    handleSelectChat,
    handleDeleteChatClick,
    handleSaveChatClick,
    handleUnsaveChatClick,
    handleDeleteConfirm,
    isChatSaved,
    editingState,
    editingTitle,
    setEditingTitle,
    handleStartRename,
    handleSubmitRename,
    handleCancelRename,
  };
}
